import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, ChevronRight, Home, Scale, Apple, HeartPulse,
  Briefcase, Trophy, Star, ArrowLeft, Sparkles, Search, X,
} from "lucide-react";
import { INDUSTRIES, getItemsByIndustry } from "../../data/categoryData";

const INDUSTRY_ICONS = {
  "real-estate": Home,
  "legal": Scale,
  "nutrition": Apple,
  "health": HeartPulse,
  "careers": Briefcase,
  "betting-gaming": Trophy,
};

const importSubIcon = (name) => {
  try {
    const mod = require("lucide-react");
    return mod[name] || mod.ChevronRight;
  } catch { return ChevronRight; }
};

const gridVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

/** Topic tiles — snappier stagger + scale for drill-down grid */
const subTopicGridVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.055, delayChildren: 0.08 },
  },
};

const subTopicCardVariants = {
  hidden: { opacity: 0, y: 28, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 380, damping: 28 },
  },
};

const BG_PATTERNS = {
  "real-estate": "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&q=80",
  "legal": "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=600&q=80",
  "nutrition": "https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=600&q=80",
  "health": "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600&q=80",
  "careers": "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=600&q=80",
  "betting-gaming": "https://images.unsplash.com/photo-1511447333015-45b65e60f6d5?w=600&q=80",
};

/** Main category-navigation copy (section + cards). Grid order follows EXPLORE_GRID_ORDER. */
const CATEGORY_NAV_COPY = {
  legal: {
    emoji: "⚖️",
    title: "Legal",
    line: "Land, marriage, business, criminal law & more",
    exploreLabel: "Explore Legal",
  },
  health: {
    emoji: "🏥",
    title: "Health",
    line: "Insurance, treatment, wellness & care",
    exploreLabel: "Explore Health",
  },
  "real-estate": {
    emoji: "🏠",
    title: "Real Estate",
    line: "Property, land, rentals & investments",
    exploreLabel: "Explore Real Estate",
  },
  careers: {
    emoji: "💼",
    title: "Careers",
    line: "Jobs, relocation, skills & opportunities",
    exploreLabel: "Explore Careers",
  },
  "betting-gaming": {
    emoji: "🎮",
    title: "Betting & Gaming",
    line: "Platforms, comparisons & insights",
    exploreLabel: "Explore Gaming",
  },
  nutrition: {
    emoji: "🥗",
    title: "Nutrition & Meal Plans",
    line: "Diets, recipes & healthy living",
    exploreLabel: "Explore Nutrition",
  },
};

const EXPLORE_GRID_ORDER = [
  "legal",
  "health",
  "real-estate",
  "careers",
  "betting-gaming",
  "nutrition",
];

const industriesInNavOrder = EXPLORE_GRID_ORDER.map((id) =>
  INDUSTRIES.find((i) => i.id === id),
).filter(Boolean);

/* ── Industry card (main grid) ───────────────────────────── */
const IndustryCard = ({ ind, onClick }) => {
  const Icon = INDUSTRY_ICONS[ind.id] || Star;
  const bgImg = BG_PATTERNS[ind.id] || "";
  const copy = CATEGORY_NAV_COPY[ind.id] ?? {
    emoji: "",
    title: ind.label,
    line: ind.comingSoonSub ?? ind.description,
    exploreLabel: `Explore ${ind.label}`,
  };

  return (
    <motion.button
      variants={cardVariants}
      type="button"
      onClick={onClick}
      className="group relative overflow-hidden rounded-2xl border border-gray-100 bg-white text-left shadow-sm transition-all duration-300 hover:border-transparent hover:shadow-2xl"
      whileHover={{ y: -6, scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
    >
      {/* Image area */}
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={bgImg}
          alt={copy.title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0.08) 100%)",
          }}
        />
        <div className="absolute bottom-3 left-3 right-3">
          <h3 className="text-lg font-black leading-tight text-white">
            <span className="mr-1.5" aria-hidden>
              {copy.emoji}
            </span>
            {copy.title}
          </h3>
          <p className="mt-1 line-clamp-2 text-xs leading-snug text-white/75">{copy.line}</p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-black/[0.06] px-4 py-3.5">
        <div className="flex min-w-0 items-center gap-2">
          <div
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
            style={{ background: `${ind.color}18` }}
          >
            <Icon size={15} style={{ color: ind.color }} />
          </div>
          <span className="truncate text-[11px] font-semibold text-gray-500 sm:text-xs">
            {getItemsByIndustry(ind.id).length} topics
          </span>
        </div>
        <span className="inline-flex shrink-0 items-center gap-1 text-sm font-black text-gray-900">
          <span aria-hidden>👉</span>
          {copy.exploreLabel}
          <ChevronRight
            size={14}
            className="text-gray-400 transition group-hover:translate-x-0.5 group-hover:text-gray-700"
          />
        </span>
      </div>

      <div
        className="absolute bottom-0 left-0 right-0 h-[3px] opacity-0 transition-opacity group-hover:opacity-100"
        style={{ background: `linear-gradient(90deg, #FFD221, ${ind.color})` }}
      />
    </motion.button>
  );
};

/* ── Subcategory topic tile (drill-down) ─────────────────── */
const SubCard = ({ item, industryId, industryColor, index }) => {
  const navigate = useNavigate();
  const SubIcon = importSubIcon(item.icon);
  const accent = item.color || industryColor || "#FFD221";

  return (
    <motion.article
      variants={subTopicCardVariants}
      layout
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(`/${industryId}/${item.slug}`);
        }
      }}
      whileTap={{ scale: 0.985 }}
      onClick={() => navigate(`/${industryId}/${item.slug}`)}
      className="group relative cursor-pointer overflow-hidden rounded-2xl border border-black/[0.07] bg-white text-left shadow-[0_6px_28px_-18px_rgba(0,0,0,0.22)] outline-none ring-[#FFD221]/0 transition-[transform,box-shadow,border-color,ring-color] duration-300 hover:z-[1] hover:-translate-y-1.5 hover:border-black/12 hover:shadow-[0_22px_48px_-28px_rgba(0,0,0,0.38)] focus-visible:ring-2 focus-visible:ring-[#FFD221]/70"
    >
      <div
        className="absolute bottom-0 left-0 top-0 w-[3px] opacity-90 transition-[width] duration-300 group-hover:w-[5px]"
        style={{
          background: `linear-gradient(180deg, ${accent}, ${accent}66)`,
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full opacity-[0.14] blur-2xl transition-opacity duration-500 group-hover:opacity-[0.22]"
        style={{ background: accent }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white via-white to-gray-50/80 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        aria-hidden
      />

      <div className="relative flex flex-col p-4 pl-[1.1rem] sm:p-5 sm:pl-6">
        <div className="flex items-start gap-3.5">
          <div className="relative shrink-0">
            <div
              className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-white to-gray-50 shadow-[0_6px_20px_-8px_rgba(0,0,0,0.2)] ring-1 ring-black/[0.06] transition duration-300 group-hover:scale-[1.05] group-hover:shadow-[0_10px_28px_-10px_rgba(0,0,0,0.28)]"
              style={{ boxShadow: `0 8px 26px -10px ${accent}66` }}
            >
              <SubIcon size={20} style={{ color: accent }} className="shrink-0" strokeWidth={2} />
            </div>
            <span
              className="absolute -left-1 -top-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-md bg-gray-950 px-1 text-[10px] font-black tabular-nums text-[#FFD221] shadow-sm"
              aria-hidden
            >
              {String(index + 1).padStart(2, "0")}
            </span>
          </div>
          <div className="min-w-0 flex-1 pt-0.5">
            <h3 className="text-[0.8125rem] font-black leading-snug tracking-tight text-gray-950 transition-colors group-hover:text-black sm:text-sm">
              {item.name}
            </h3>
            <p className="mt-1.5 line-clamp-2 text-[11px] font-medium leading-relaxed text-gray-500 sm:text-xs">
              {item.description}
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2 pt-1">
            <ChevronRight
              size={18}
              className="text-gray-300 transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-gray-900"
              strokeWidth={2.5}
            />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between border-t border-black/[0.05] pt-3">
          <span className="text-[10px] font-black uppercase tracking-[0.15em] text-gray-400">
            Mini-site
          </span>
          <span
            className="inline-flex items-center gap-1 text-[11px] font-black sm:text-xs"
            style={{ color: accent }}
          >
            Open guide
            <ArrowRight size={12} className="transition-transform group-hover:translate-x-0.5" strokeWidth={2.5} />
          </span>
        </div>
      </div>

      <div
        className="absolute bottom-0 left-0 right-0 h-0.5 scale-x-0 bg-gradient-to-r from-transparent via-[#FFD221] to-transparent opacity-0 transition duration-500 group-hover:scale-x-100 group-hover:opacity-100"
        aria-hidden
      />
    </motion.article>
  );
};

/* ── Main component ─────────────────────────────────────── */
const Categories = ({ onItemSelect }) => {
  const [activeId, setActiveId] = useState(null);
  const [topicQuery, setTopicQuery] = useState("");
  const activeIndustry = INDUSTRIES.find((c) => c.id === activeId) ?? null;
  const activeNavCopy = activeId ? CATEGORY_NAV_COPY[activeId] : null;
  const items = useMemo(
    () => (activeId ? getItemsByIndustry(activeId) : []),
    [activeId],
  );
  const sectionRef = useRef(null);

  const filteredItems = useMemo(() => {
    const q = topicQuery.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (i) =>
        i.name.toLowerCase().includes(q) ||
        i.description.toLowerCase().includes(q) ||
        String(i.slug ?? "").toLowerCase().includes(q),
    );
  }, [items, topicQuery]);

  useEffect(() => {
    setTopicQuery("");
  }, [activeId]);

  const handleIndustryClick = (ind) => {
    setActiveId(ind.id);
  };

  /** When drilling into sub-topics, align section top with viewport (respects scroll-margin for fixed nav). */
  useEffect(() => {
    if (!activeId) return;
    requestAnimationFrame(() => {
      sectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, [activeId]);

  return (
    <section
      ref={sectionRef}
      id="categories"
      className="w-full scroll-mt-28 overflow-hidden bg-[#f7f4ec] px-4 pb-16 pt-12 sm:px-6 sm:pb-20 sm:pt-14 lg:px-8"
    >
      <div className="mx-auto max-w-8xl">

        {/* ── Header ──────────────────────────────────────── */}
        <motion.div
          className="relative mb-12 text-center sm:mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <div
            className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[min(22rem,55vw)] w-[min(100%,40rem)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-b from-[#FFD221]/25 via-[#FFD221]/8 to-transparent blur-3xl"
            aria-hidden
          />
          <div
            className="absolute inset-x-0 top-12 mx-auto max-w-md h-px bg-gradient-to-r from-transparent to-transparent pointer-events-none via-black/10"
            aria-hidden
          />

          <div className="relative mx-auto max-w-3xl">
            <motion.span
              layout="position"
              className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#FFD221]/35 bg-white/90 px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.2em] text-[#9a7b00] shadow-[0_8px_30px_-18px_rgba(201,160,0,0.45)] backdrop-blur-sm sm:text-xs sm:tracking-[0.25em]"
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.05, duration: 0.4 }}
            >
              <motion.span
                animate={{ rotate: [0, 12, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="text-[#E5B800]"
                aria-hidden
              >
                <Sparkles size={14} strokeWidth={2.5} />
              </motion.span>
              {activeId ? "Inside this ecosystem" : "Category universe"}
            </motion.span>

            {activeId ? (
              <>
                <p className="mb-2 text-[11px] font-black uppercase tracking-[0.28em] text-gray-400">
                  Now exploring
                </p>
                <h2
                  className="mx-auto max-w-[22ch] text-[clamp(1.85rem,4.2vw+0.85rem,3.35rem)] font-black leading-[1.06] tracking-tight text-gray-950 sm:max-w-none"
                  style={{
                    textShadow: "0 1px 0 rgba(255,255,255,0.6)",
                  }}
                >
                  <span className="block sm:inline sm:pr-2">Topics in</span>
                  <span
                    className="inline-block relative mt-1 sm:mt-0"
                    style={{ color: activeIndustry?.color ?? "#FFD221" }}
                  >
                    <span className="relative z-10">
                      {activeNavCopy?.title ?? activeIndustry?.label}
                    </span>
                    <span
                      className="absolute -bottom-1 left-0 right-0 mx-auto h-[0.28rem] max-w-full rounded-full opacity-40"
                      style={{
                        background: `linear-gradient(90deg, transparent, ${activeIndustry?.color ?? "#FFD221"}, transparent)`,
                      }}
                      aria-hidden
                    />
                  </span>
                </h2>
                {activeIndustry?.comingSoonSub ? (
                  <p className="mx-auto mt-5 max-w-lg text-sm font-medium leading-relaxed text-gray-600 sm:text-[0.95rem]">
                    {activeIndustry.comingSoonSub}
                  </p>
                ) : null}
              </>
            ) : (
              <>
                <h2 className="mx-auto font-black tracking-tight text-gray-950">
                  <span className="block text-[clamp(1.85rem,4vw+1rem,3.35rem)] leading-[1.08]">
                    Explore{" "}
                    <span className="bg-gradient-to-r from-[#b8860b] via-[#FFD221] to-[#ffec80] bg-clip-text text-transparent">
                      Categories
                    </span>
                  </span>
                </h2>
                <div
                  className="mx-auto mt-6 flex h-1 w-24 justify-center gap-1 sm:mt-7 sm:w-28"
                  aria-hidden
                >
                  <span className="h-full w-6 rounded-full bg-black/[0.08]" />
                  <span className="h-full flex-1 rounded-full bg-gradient-to-r from-[#FFD221] via-[#ffdf44] to-[#FFD221]" />
                  <span className="h-full w-6 rounded-full bg-black/[0.08]" />
                </div>
                <p className="mx-auto mt-6 flex max-w-2xl items-start justify-center gap-2 text-[0.9375rem] leading-relaxed text-gray-600 sm:text-base">
                  <span className="select-none text-lg leading-6" aria-hidden>
                    💡
                  </span>
                  <span className="text-left sm:text-center">
                    Each category leads to its own mini-site, as defined in your PRD.
                  </span>
                </p>
              </>
            )}
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          {!activeId ? (
            /* ── Industry grid ──────────────────────────── */
            <motion.div
              key="industry-cards"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
            >
              <motion.div
                className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
                variants={gridVariants}
                initial="hidden"
                animate="visible"
              >
                {industriesInNavOrder.map((ind) => (
                  <IndustryCard key={ind.id} ind={ind} onClick={() => handleIndustryClick(ind)} />
                ))}
              </motion.div>
            </motion.div>
          ) : (
            <motion.div
              key={activeId}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div
                className="relative overflow-hidden rounded-[1.75rem] border border-black/[0.08] bg-white/80 shadow-[0_28px_90px_-52px_rgba(0,0,0,0.55)] backdrop-blur-xl sm:rounded-[2rem]"
                style={{
                  boxShadow: `0 32px 100px -52px ${activeIndustry?.color ?? "#000"}33`,
                }}
              >
                <div
                  className="pointer-events-none absolute inset-0 opacity-[0.11] transition-opacity duration-700"
                  style={{
                    backgroundImage: `url(${BG_PATTERNS[activeId] ?? ""})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                  aria-hidden
                />
                <div
                  className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/95 via-[#f7f4ec]/92 to-white/[0.97]"
                  aria-hidden
                />
                <div
                  className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full opacity-[0.18] blur-3xl"
                  style={{ background: activeIndustry?.color }}
                  aria-hidden
                />
                <div
                  className="pointer-events-none absolute -right-16 bottom-0 h-56 w-56 rounded-full bg-[#FFD221]/20 blur-3xl"
                  aria-hidden
                />

                <div className="relative p-4 sm:p-6 lg:p-8">
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35 }}
                    className="mb-6 flex flex-col gap-4 border-b border-black/[0.06] pb-6 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3"
                  >
                    <button
                      type="button"
                      onClick={() => setActiveId(null)}
                      className="group inline-flex items-center justify-center gap-2 rounded-2xl border border-black/10 bg-white/90 px-4 py-3 text-sm font-black text-gray-800 shadow-[0_4px_24px_-16px_rgba(0,0,0,0.2)] transition hover:border-black/25 hover:bg-white sm:justify-start"
                    >
                      <ArrowLeft size={16} className="transition group-hover:-translate-x-0.5" />
                      All categories
                    </button>

                    <div className="flex min-w-0 flex-1 items-center justify-center gap-3 sm:justify-center lg:flex-none">
                      <div
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl shadow-inner ring-1 ring-black/[0.06]"
                        style={{
                          background: `linear-gradient(145deg, ${activeIndustry?.color}22, white)`,
                        }}
                      >
                        {React.createElement(INDUSTRY_ICONS[activeId] || Star, {
                          size: 20,
                          style: { color: activeIndustry?.color },
                          strokeWidth: 2.25,
                        })}
                      </div>
                      <div className="min-w-0 text-center sm:text-left">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
                          Active lane
                        </p>
                        <p className="truncate text-base font-black text-gray-950">
                          {activeNavCopy ? (
                            <>
                              <span aria-hidden>{activeNavCopy.emoji} </span>
                              {activeNavCopy.title}
                            </>
                          ) : (
                            activeIndustry?.label
                          )}
                        </p>
                      </div>
                    </div>

                    <Link
                      to={`/${activeId}`}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-black text-black shadow-[0_8px_28px_-14px_rgba(0,0,0,0.35)] transition hover:brightness-105 sm:shrink-0"
                      style={{ background: "#FFD221" }}
                    >
                      {activeNavCopy?.exploreLabel ?? "View hub"}
                      <ArrowRight size={16} strokeWidth={2.5} />
                    </Link>
                  </motion.div>

                  <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="relative flex-1 lg:max-w-md">
                      <Search
                        className="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-gray-400"
                        size={18}
                        strokeWidth={2}
                        aria-hidden
                      />
                      <input
                        type="search"
                        value={topicQuery}
                        onChange={(e) => setTopicQuery(e.target.value)}
                        placeholder="Filter topics by name or keyword…"
                        className="w-full rounded-2xl border border-black/10 bg-white/95 py-3.5 pl-11 pr-11 text-sm font-medium text-gray-900 shadow-inner outline-none ring-[#FFD221]/0 transition placeholder:text-gray-400 focus:border-black/15 focus:ring-2 focus:ring-[#FFD221]/45"
                        aria-label="Filter topics"
                      />
                      <AnimatePresence>
                        {topicQuery ? (
                          <motion.button
                            type="button"
                            initial={{ opacity: 0, scale: 0.85 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.85 }}
                            transition={{ duration: 0.15 }}
                            className="absolute right-3 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-gray-500 hover:bg-black/[0.05] hover:text-black"
                            onClick={() => setTopicQuery("")}
                            aria-label="Clear filter"
                          >
                            <X size={16} />
                          </motion.button>
                        ) : null}
                      </AnimatePresence>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                      <span
                        className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-black px-3.5 py-1.5 text-[11px] font-black uppercase tracking-wider text-[#FFD221]"
                      >
                        {filteredItems.length === items.length ? (
                          <>{items.length} topic guides</>
                        ) : (
                          <>
                            {filteredItems.length} match{filteredItems.length === 1 ? "" : "es"}
                            <span className="font-semibold text-white/70">·</span>
                            <span className="font-semibold text-white/80">{items.length} total</span>
                          </>
                        )}
                      </span>
                      <span className="rounded-full bg-white/90 px-3 py-1.5 text-[11px] font-bold text-gray-600 ring-1 ring-black/[0.06]">
                        One tap → full mini-site
                      </span>
                    </div>
                  </div>

                  <AnimatePresence mode="popLayout">
                    {filteredItems.length === 0 ? (
                      <motion.div
                        key="no-topics"
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.98 }}
                        transition={{ duration: 0.22 }}
                        className="flex flex-col items-center rounded-2xl border border-dashed border-black/15 bg-white/60 px-6 py-16 text-center"
                      >
                        <Search className="mb-4 text-gray-300" size={40} strokeWidth={1.25} />
                        <p className="text-base font-black text-gray-900">No topics match that filter</p>
                        <p className="mt-2 max-w-sm text-sm text-gray-500">
                          Try another keyword or clear the search to see every guide in this category.
                        </p>
                        <button
                          type="button"
                          onClick={() => setTopicQuery("")}
                          className="mt-6 rounded-xl bg-[#FFD221] px-5 py-2.5 text-sm font-black text-black shadow-sm transition hover:bg-[#ffdf44]"
                        >
                          Clear filter
                        </button>
                      </motion.div>
                    ) : (
                      <motion.div
                        key={`topic-grid-${activeId}`}
                        className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4"
                        variants={subTopicGridVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        {filteredItems.map((item, index) => (
                          <SubCard
                            key={item.id}
                            item={item}
                            industryId={activeId}
                            industryColor={activeIndustry?.color ?? "#FFD221"}
                            index={index}
                          />
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35, duration: 0.45 }}
                    className="mt-10 flex flex-col items-center justify-center gap-4 border-t border-black/[0.06] pt-8 sm:flex-row"
                  >
                    <p className="max-w-md text-center text-sm font-medium text-gray-500 sm:text-left">
                      Prefer the full hub? Open the industry overview for hero content, sponsors, and every lane in one scroll.
                    </p>
                    <Link
                      to={`/${activeId}`}
                      className="inline-flex shrink-0 items-center gap-2 rounded-2xl border-2 border-black/10 bg-white px-6 py-3.5 text-sm font-black text-gray-900 shadow-sm transition hover:border-[#FFD221] hover:bg-[#FFD221] hover:text-black hover:shadow-md"
                    >
                      Open {activeNavCopy?.title ?? activeIndustry?.label} hub
                      <ArrowRight size={16} strokeWidth={2.5} />
                    </Link>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Categories;
